import { el, clear, debounce } from '../utils/dom.js';
import { dataTable } from './table.js';
import { openModal, confirm } from './modal.js';
import { field, readForm } from './form.js';
import { skeletonRows } from './skeleton.js';
import { ok, err } from './toast.js';

const skeletonTable = () => skeletonRows(5, 5);

/*
 * crudPage(outlet, {
 *   title, api, columns, formFields(item),
 *   filters?: extra query params object,
 *   listExtraQuery?: () => extra params,
 *   transformBeforeSave?: (data, isEdit) => transformed,
 *   pageSize?: 25,
 *   canEdit?: true, canDelete?: true,
 * })
 */
export async function crudPage(outlet, opts) {
  clear(outlet);
  const state = { page: 1, q: '', extra: opts.filters || {} };
  const refreshFns = [];

  const head = el('div', { class: 'page-head' },
    el('h1', {}, opts.title),
    el('button', { class: 'btn primary', onclick: () => openForm(null) }, '+ Nuevo'),
  );
  outlet.appendChild(head);

  const toolbar = el('div', { class: 'toolbar' },
    el('input', {
      class: 'input grow',
      placeholder: 'Buscar...',
      oninput: debounce((e) => { state.q = e.target.value; state.page = 1; refresh(); }, 300),
    }),
  );
  outlet.appendChild(toolbar);

  const tableHost = el('div');
  outlet.appendChild(tableHost);

  async function refresh() {
    tableHost.replaceChildren(skeletonTable());
    const params = { page: state.page, limit: opts.pageSize || 25, q: state.q || undefined, ...state.extra };
    if (opts.listExtraQuery) Object.assign(params, opts.listExtraQuery());
    try {
      const res = await opts.api.list(params);
      const rows = res.data || res.rows || res;
      const pagination = res.pagination;

      const cols = [
        ...opts.columns,
        {
          key: '_actions', label: '', className: 'right',
          render: (r) => el('div', { class: 'row-actions' },
            opts.canEdit !== false ? el('button', {
              class: 'btn sm', onclick: () => openForm(r),
            }, 'Editar') : null,
            opts.canDelete !== false ? el('button', {
              class: 'btn sm danger',
              onclick: async () => {
                if (await confirm({ title: 'Eliminar', message: `Eliminar "${r.name || r.full_name || r.code || r.id}"?`, danger: true, confirmText: 'Eliminar' })) {
                  try {
                    await opts.api.remove(r.id);
                    ok('Eliminado');
                    refresh();
                  } catch (ex) { err(ex.message); }
                }
              },
            }, 'Eliminar') : null,
          ),
        },
      ];

      tableHost.replaceChildren(dataTable({
        columns: cols,
        rows,
        pagination: pagination ? {
          page: pagination.page,
          pages: pagination.pages,
          onPage: (p) => { state.page = p; refresh(); },
        } : null,
      }));
    } catch (ex) {
      tableHost.innerHTML = `<div class="empty">Error: ${ex.message}</div>`;
    }
  }
  refreshFns.push(refresh);

  function openForm(item) {
    const isEdit = !!item;
    const form = el('form', {
      onsubmit: async (e) => {
        e.preventDefault();
        let data = readForm(form);
        if (opts.transformBeforeSave) data = opts.transformBeforeSave(data, isEdit, item);
        try {
          if (isEdit) await opts.api.update(item.id, data);
          else        await opts.api.create(data);
          ok(isEdit ? 'Actualizado' : 'Creado');
          modal.close();
          refresh();
        } catch (ex) {
          err(ex.message);
        }
      },
    });

    const fields = opts.formFields(item || {});
    const grid = el('div', { class: 'form-grid' });
    for (const f of fields) grid.appendChild(field(f));
    form.appendChild(grid);

    const submitBtn = el('button', { class: 'btn primary', type: 'submit' }, isEdit ? 'Guardar' : 'Crear');
    const cancelBtn = el('button', { class: 'btn', type: 'button', onclick: () => modal.close() }, 'Cancelar');

    const modal = openModal({
      title: (isEdit ? 'Editar ' : 'Nuevo ') + opts.title.toLowerCase(),
      body: form,
      footer: [cancelBtn, submitBtn],
    });
  }

  refresh();
  return { refresh };
}
